import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/services/api";
import { components } from "@libertify/api-types/media";

type ThemeResponse = components["schemas"]["ThemeResponse"];
type PaginatedThemeResponse =
  components["schemas"]["PaginatedResponse_ThemeResponse_"];
type CreateThemeRequest = components["schemas"]["CreateThemeRequest"];
type UpdateThemeRequest = components["schemas"]["UpdateThemeRequest"];
type LayoutTemplateResponse = components["schemas"]["LayoutTemplateResponse"];
type UpsertLayoutTemplateRequest =
  components["schemas"]["UpsertLayoutTemplateRequest"];
type SlideType = components["schemas"]["SlideType"];

export type {
  ThemeResponse,
  PaginatedThemeResponse,
  CreateThemeRequest,
  UpdateThemeRequest,
  LayoutTemplateResponse,
  UpsertLayoutTemplateRequest,
};

const THEMES_QUERY_KEY = "themes";

export const usePublicThemes = (page = 1, size = 50) => {
  return useQuery<PaginatedThemeResponse>({
    queryKey: [THEMES_QUERY_KEY, "public", page, size],
    queryFn: async () => {
      const response = await axiosInstance.get<PaginatedThemeResponse>(
        "/media/themes",
        { params: { page, size } }
      );
      return response.data;
    },
  });
};

export const useOrgThemes = (
  organizationId: string | undefined,
  page = 1,
  size = 50
) => {
  return useQuery<PaginatedThemeResponse>({
    queryKey: [THEMES_QUERY_KEY, "org", organizationId, page, size],
    queryFn: async () => {
      const response = await axiosInstance.get<PaginatedThemeResponse>(
        `/media/${organizationId}/themes`,
        { params: { page, size, public: false } }
      );
      return response.data;
    },
    enabled: !!organizationId,
  });
};

export const useCreateTheme = (organizationId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<ThemeResponse, Error, CreateThemeRequest>({
    mutationFn: async (data) => {
      const response = await axiosInstance.post<ThemeResponse>(
        `/media/${organizationId}/themes`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [THEMES_QUERY_KEY, "org", organizationId],
        })
        .catch(console.error);
    },
  });
};

export const useCloneTheme = (organizationId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<ThemeResponse, Error, string>({
    mutationFn: async (themeId) => {
      const response = await axiosInstance.post<ThemeResponse>(
        `/media/${organizationId}/themes/${themeId}/clone`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [THEMES_QUERY_KEY, "org", organizationId],
        })
        .catch(console.error);
    },
  });
};

export const useUpdateTheme = (organizationId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<
    ThemeResponse,
    Error,
    { themeId: string; data: UpdateThemeRequest }
  >({
    mutationFn: async ({ themeId, data }) => {
      const response = await axiosInstance.patch<ThemeResponse>(
        `/media/${organizationId}/themes/${themeId}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [THEMES_QUERY_KEY],
        })
        .catch(console.error);
    },
  });
};

export const useDeleteTheme = (organizationId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (themeId) => {
      await axiosInstance.delete(`/media/${organizationId}/themes/${themeId}`);
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [THEMES_QUERY_KEY],
        })
        .catch(console.error);
    },
  });
};

export const useSaveLayout = (organizationId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<
    LayoutTemplateResponse,
    Error,
    {
      themeId: string;
      slideType: SlideType;
      data: UpsertLayoutTemplateRequest;
    }
  >({
    mutationFn: async ({ themeId, slideType, data }) => {
      const response = await axiosInstance.put<LayoutTemplateResponse>(
        `/media/${organizationId}/themes/${themeId}/layouts/${slideType}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [THEMES_QUERY_KEY],
        })
        .catch(console.error);
    },
  });
};
